import { useContext, useEffect, useMemo, useState } from "react";

import { useGetCareEvent } from "api/hooks/useGetCareEvent";
import { useGetCareEventsBilling } from "api/hooks/useGetCareEventsBilling";
import type { CareEventBilling, Status } from "api/schemas/CareEventBilling";
import { CurrentPatientContext } from "utils/contexts";

export type Dialog = { show: false } | { show: true; status: Status };

const initialDialogState: Dialog = { show: false };

type StatusCounts = Record<Status, number>;

export const useBillingState = () => {
  const { patient, patientLoading, setPatientId } = useContext(CurrentPatientContext);

  const [filter, setFilter] = useState<Status>("pending");
  const [selectedCareEvent, setSelectedCareEvent] = useState<CareEventBilling | null>(null);
  const [selectedCareEventIDs, setSelectedCareEventIDs] = useState<number[]>([]);
  const [showDialog, setShowDialog] = useState<Dialog>(initialDialogState);
  const [error, setError] = useState<string | null>(null);

  const { data: careEventsBilling } = useGetCareEventsBilling();

  const { data: careEvent, isLoading: isLoadingCareEvent } = useGetCareEvent(selectedCareEvent?.care_event_id ?? 0, {
    enabled: !!selectedCareEvent?.care_event_id,
  });

  const statusCounts = useMemo<StatusCounts>(() => {
    const counts: StatusCounts = { pending: 0, completed: 0, discarded: 0 };
    (careEventsBilling ?? []).forEach(item => {
      counts[item.status] += 1;
    });
    return counts;
  }, [careEventsBilling]);

  const filteredNotes = useMemo(() => {
    return (careEventsBilling ?? []).filter(note => note.status === filter);
  }, [careEventsBilling, filter]);

  useEffect(() => {
    setSelectedCareEventIDs([]);
  }, [careEventsBilling]);

  useEffect(() => {
    setPatientId(careEvent ? careEvent.patient_id : null);
  }, [careEvent, setPatientId]);

  return {
    careEvent,
    error,
    filter,
    filteredNotes,
    isLoadingCareEvent,
    patient,
    patientLoading,
    selectedCareEvent,
    selectedCareEventIDs,
    setError,
    setFilter,
    setSelectedCareEvent,
    setSelectedCareEventIDs,
    setShowDialog,
    showCareEvent: !!selectedCareEvent,
    showDialog,
    statusCounts,
  };
};
